/**
 * @file
 * JavaScript entrypoint file for the theme.
 *
 * All JavaScript added to the theme must go through this Webpack entrypoint.
 * Modules are included with import statements. Remove unused modules.
 * Import any module-specific CSS as well, and Webpack will bundle it in
 * /assets/dist/app.css. Module CSS paths are relative to the node_modules
 * folder, i.e. '@glidejs/glide/dist/css/glide.core.css' for Glide's CSS.
 */

import './compat/ie11';
import { Fancybox } from '@fancyapps/ui';
import '@fancyapps/ui/dist/fancybox.css';
import { prefersReducedMotion } from './utils/prefers-reduced-motion';
import AccessibleMenu from './modules/accessible-menu';
import Accordion from './modules/accordion';
import {
  AnimateCount,
  AnimateSingle,
  AnimateSequence,
} from './modules/animation';
import ButtonToggle from './modules/button-toggle';
import HeaderState from './modules/header-state';
import ImageGrid from './modules/image-grid';
import JumpLink from './modules/jump-link';
import JumpNav from './modules/jump-nav';
import MenuOverviewLink from './modules/menu-overview-link';
import MoreOrLess from './modules/more-or-less';
import SlidingToggle from './modules/sliding-toggle';
import SmoothScroll from './modules/smooth-scroll';
import SystemMessages from './modules/d8/system-messages';
import Tab from './modules/tab';
import Table from './modules/table';
import Slider from './modules/slider';
import OhoVideo from './modules/video';
import ScrollTracker from './modules/scroll-tracker';
import CookieBanner from './modules/cookie-banner';

(() => {

  /**
   * Create standard tabs.
   */
   const tabs = new Tab();
   tabs.add('.tabs').run();

   /**
    * Wrap tables for responsive styles.
    */
   const tables = new Table();
   tables.add('#main-content table').run();

  /**
   * Add the sticky header.
   */
  const headerState = new HeaderState();
  headerState.add('.site-header').run();

  /**
   * Init The Cookie Banner
   */
   CookieBanner();

  /**
   * Global Button Toggle controlled by data attrs.
   */
  const globalButtonToggle = new ButtonToggle();
  globalButtonToggle.add('.button-toggle').run();

  /**
   * Autoplay Videos
   */
  if (prefersReducedMotion() === false) {
    const video = new OhoVideo();
    video.add('.autoplay-video-container').run();
  }

  const accordion = new Accordion();
  accordion.add('.accordion').run();

  // If there are tables inside of an accordion, fire the init
  accordion.on('toggle', event => {
    let tables = event.accordion.querySelectorAll('table');
    if (event.accordion.isOpen == true && tables.length) {
      tables.forEach(accordionTable => {
        accordionTable.toggleResponsiveTable()
      });
    }
  });

  /**
   * Add the accessible Main menu.
   */
  const accessibleMainMenu = new AccessibleMenu();
  accessibleMainMenu.add('.main-menu').run();

  /**
   * Add the accessible Audience menu.
   */
  const accessibleAudienceMenu = new AccessibleMenu();
  accessibleAudienceMenu.add('.audience-menu').run();

  /**
   * Add the accessible sidebar menu.
   */
  const accessibleSidebarMenu = new AccessibleMenu({
    expandActiveTrail: true,
  });
  accessibleSidebarMenu.add('.sidebar-menu').run();

  /**
   * Create standard image grids.
   */
  const imageGrid = new ImageGrid();
  imageGrid.add('.image-grid').run();

  /**
   * Create jump link scrolling.
   */
  const jumpLinks = new JumpLink();
  jumpLinks.add('[href^="#"]:not([href="#"])').run();

  /**
   * Create the jump nav.
   */
  new JumpNav({
    targetSelector: 'h2',
  }).create('.jump-nav');

  /**
   * Add standard animations.
   */
  let singleAnimation;
  let sequenceAnimation;
  let countAnimation;

  if (prefersReducedMotion() === false) {
    singleAnimation = new AnimateSingle();
    singleAnimation.add('.oho-animate-single').run();

    sequenceAnimation = new AnimateSequence();
    sequenceAnimation.add('.oho-animate-sequence').run();

    countAnimation = new AnimateCount();
    countAnimation.add('.oho-animate-count').run();
  }

  // Glossary JS
  singleAnimation.on('animate', event => {
    if (event.item.id === 'animation-glossary-button-state') {
      const statusArray = [];
      event.item.classList.forEach(classState => {
        statusArray.push(`<p>${classState}</p>`);
      });
      document.getElementById(
        'animation-glossary-button-state',
      ).innerHTML = statusArray.join('');
    }
  });

  /**
   * Create standard more or less.
   */
  const moreOrLess = new MoreOrLess();
  moreOrLess.add('.more-less').run();

  /**
   * Create standard sliding toggles.
   */
  const slidingToggle = new SlidingToggle();
  slidingToggle.add('.slide-toggle').run();

  /**
   * Smooth scrolling demo page.
   */
  const smoothScroll = new SmoothScroll();
  const scrollButtons = document.querySelectorAll(
    '[data-smooth-scroll-button]',
  );
  scrollButtons.forEach(button => {
    const target = button.getAttribute('data-smooth-scroll-button');
    const element = document.querySelector(
      `[data-smooth-scroll-target="${target}"]`,
    );
    button.addEventListener('click', () => smoothScroll.scrollTo(element));
  });

  /**
   * Profile Slider.
   */
  const slider = new Slider({
    sliderOpts: {
      slidesPerView: 1,
      watchSlidesVisibility: true,
      spaceBetween: 40,
      centeredSlides: false,
      loop: true,
      navigation: {
        nextEl: '.slider-navigation__next',
        prevEl: '.slider-navigation__prev',
      },
      breakpoints: {
        640: {
          slidesPerView: 2,
        },
        768: {
          slidesPerView: 3,
        },
        1024: {
          slidesPerView: 4,
        },
      },
    },
  });
  slider.add('.profile-feature').run();

  /**
   * Hero Scroll Tracker
   */
  if (prefersReducedMotion() === false) {
    const heroScrollTracker = new ScrollTracker({
      scrollTrackerItem: '.hero__media',
    });
    heroScrollTracker.add('.hero--parallax').run();
  }

  /**
   * Allow system messages to be dismissable.
   */
  const systemMessages = new SystemMessages();
  systemMessages.add('.messages').run();

  /**
   * Add a class to the body to style against if this JavaScript did not load
   * correctly or has an error which blocks JavaScript execution.
   */
  document.querySelector('html').classList.add('oho-js');
})();

/**
 * Tells Fancybox not to autoplay videos.
 */
Fancybox.bind('[data-fancybox]', {
  Html: {
    video: {
      autoplay: false,
    },
  },
});
